import { smoothData } from "~/util/statistics.util";
import { SensorData, FormattedChartData } from "./types";
import { safeNumber, createChartPoint } from "./dataProcessing.utils";
import {
  createDefaultOrientationSample,
  createOrientationSample,
  normaliseWrappedAxis,
  toDisplayAxisValue,
  type AccelerometerOrientationSample,
} from "./accelerometerOrientation.utils";

interface AccelerometerResult {
  x: FormattedChartData[];
  y: FormattedChartData[];
  z: FormattedChartData[];
}

interface SingleTestAccelerometerResult {
  lines: FormattedChartData[];
  orientationSamples: AccelerometerOrientationSample[];
}

export const processAccelerometerData = (
  testsData: (SensorData | null)[],
  testNames: string[]
): AccelerometerResult => {
  if (!testsData.some(Boolean)) {
    return { x: [], y: [], z: [] };
  }

  const result: AccelerometerResult = { x: [], y: [], z: [] };

  testsData.forEach((testData, testIndex) => {
    if (!testData?.accelerometer?.length) {
      return;
    }

    const testName = testNames[testIndex];

    // Extract all three axes in one pass
    const axes: { x: number[]; y: number[]; z: number[] } = {
      x: [],
      y: [],
      z: [],
    };

    testData.accelerometer.forEach((point) => {
      axes.x.push(normaliseWrappedAxis(safeNumber(point.x)));
      axes.y.push(normaliseWrappedAxis(safeNumber(point.y)));
      axes.z.push(normaliseWrappedAxis(safeNumber(point.z)));
    });

    // Process each axis
    (["x", "y", "z"] as const).forEach((axis) => {
      const smoothedValues = smoothData(axes[axis], 3);
      result[axis].push({
        id: testName,
        data: smoothedValues.map((val, idx) =>
          createChartPoint(idx, toDisplayAxisValue(val))
        ),
      });
    });
  });

  return result;
};

export const processSingleTestAccelerometerData = (
  testsData: (SensorData | null)[]
): SingleTestAccelerometerResult => {
  if (!testsData.some(Boolean)) {
    return {
      lines: [],
      orientationSamples: [createDefaultOrientationSample()],
    };
  }

  const axisConfig = {
    x: { color: "#3182bd", label: "X" },
    y: { color: "#ff8c00", label: "Y" },
    z: { color: "#2ca02c", label: "Z" },
  };

  const firstTestWithAccelerometerData = testsData.find(
    (testData) => testData?.accelerometer?.length
  );

  if (!firstTestWithAccelerometerData?.accelerometer?.length) {
    return {
      lines: [],
      orientationSamples: [createDefaultOrientationSample()],
    };
  }

  const normalizedAxes: { x: number[]; y: number[]; z: number[] } = {
    x: [],
    y: [],
    z: [],
  };

  firstTestWithAccelerometerData.accelerometer.forEach((point) => {
    normalizedAxes.x.push(normaliseWrappedAxis(safeNumber(point.x)));
    normalizedAxes.y.push(normaliseWrappedAxis(safeNumber(point.y)));
    normalizedAxes.z.push(normaliseWrappedAxis(safeNumber(point.z)));
  });

  const smoothedAxes = {
    x: smoothData(normalizedAxes.x, 3),
    y: smoothData(normalizedAxes.y, 3),
    z: smoothData(normalizedAxes.z, 3),
  };

  const lines = (["x", "y", "z"] as const).map((axis) => {
    const { color, label } = axisConfig[axis];

    return {
      id: label,
      color,
      data: smoothedAxes[axis].map((value, idx) =>
        createChartPoint(idx, toDisplayAxisValue(value))
      ),
    };
  });

  return {
    lines,
    orientationSamples: smoothedAxes.x.map((_, sampleIndex) =>
      createOrientationSample(sampleIndex, {
        x: smoothedAxes.x[sampleIndex],
        y: smoothedAxes.y[sampleIndex],
        z: smoothedAxes.z[sampleIndex],
      })
    ),
  };
};
